import React, { useState } from 'react';
import { X } from 'lucide-react';
import {
  StyledCard, FormGrid, StyledFormGroup, TooltipWrapper, InfoIcon,
  NcmTag, RegionButton, DropdownList, DropdownItem
} from './style';

const REGION_OPTIONS = [
  { value: 'CENTRO_OESTE', label: 'Centro-Oeste' },
  { value: 'NORDESTE', label: 'Nordeste' },
  { value: 'NORTE', label: 'Norte' },
  { value: 'SUDESTE', label: 'Sudeste' },
  { value: 'SUL', label: 'Sul' },
];

const LabelWithInfo = ({ label, tooltip }: { label: string, tooltip: string }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <label style={{ marginBottom: 0 }}>{label}</label>
    <TooltipWrapper data-tooltip={tooltip}>
      <InfoIcon>i</InfoIcon>
    </TooltipWrapper>
  </div>
);

export const ScenarioCard = ({ scenario, index, updateScenario, removeScenario, allStates, taxCodes, classTribOptions }: any) => {
  const [stateSearch, setStateSearch] = useState("");
  const [isStateListOpen, setIsStateListOpen] = useState(false);

  const codes = taxCodes ?? { cstIcms: [], cstIbsCbs: [] };
  const selectedIbsCbs = (codes.cstIbsCbs ?? []).find((c: any) => c.code === scenario.cstIBS_CBS);
  const classTribFiltered = selectedIbsCbs
    ? (classTribOptions ?? []).filter((c: any) => c.cstIbsCbsId === selectedIbsCbs.id)
    : [];

  return (
    <StyledCard style={{ border: '1px solid #eee', position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <NcmTag style={{ border: 'none', fontWeight: 'bold', textTransform: 'uppercase', background: '#e3f2fd' }}>
          Cenário {index + 1}
        </NcmTag>
        {index > 0 && (
          <div onClick={() => removeScenario(scenario.id)} style={{ cursor: 'pointer', background: '#FEECEC', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={14} color="#F75A68" />
          </div>
        )}
      </div>

      <FormGrid cols={2}>
        <StyledFormGroup>
          <LabelWithInfo label="CST ICMS" tooltip="Código de Situação Tributária do ICMS" />
          <select value={scenario.cstICMS} onChange={(e) => updateScenario(scenario.id, 'cstICMS', e.target.value)}>
            <option value="">Selecione</option>
            {(codes.cstIcms ?? []).map((opt: any) => (
              <option key={opt.code} value={opt.code}>{opt.code} — {opt.description}</option>
            ))}
          </select>
        </StyledFormGroup>

        <StyledFormGroup>
          <LabelWithInfo label="Alíquota ICMS (%)" tooltip="Alíquota interna ou interestadual" />
          <input type="number" value={scenario.aliquotaICMS} onChange={(e) => updateScenario(scenario.id, 'aliquotaICMS', Number(e.target.value))} />
        </StyledFormGroup>

        <StyledFormGroup>
          <LabelWithInfo label="Base de cálculo (%)" tooltip="Percentual da base de cálculo" />
          <input type="number" value={scenario.baseDeCalculo} onChange={(e) => updateScenario(scenario.id, 'baseDeCalculo', Number(e.target.value))} />
        </StyledFormGroup>

        <StyledFormGroup>
          <LabelWithInfo label="Diferencial de alíquota" tooltip="Escolha se aplica DIFAL, Substituição Tributária ou Operação Normal" />
          <select
            value={
              scenario.diferencialAliquota ? 'difal' :
                scenario.substituicaoTributaria ? 'st' : 'normal'
            }
            onChange={(e) => {
              const val = e.target.value;
              updateScenario(scenario.id, 'diferencialAliquota', val === 'difal');
              updateScenario(scenario.id, 'substituicaoTributaria', val === 'st');
            }}
          >
            <option value="normal">Não se aplica</option>
            <option value="difal">Aplicar DIFAL</option>
            <option value="st">ST — Substituição tributária</option>
          </select>
        </StyledFormGroup>

        <StyledFormGroup>
          <LabelWithInfo label="CST IBS/CBS" tooltip="Novo imposto da reforma tributária" />
          <select
            value={scenario.cstIBS_CBS}
            onChange={(e) => {
              updateScenario(scenario.id, 'cstIBS_CBS', e.target.value);
              updateScenario(scenario.id, 'classificacaoTributaria', '');
            }}
          >
            <option value="">Selecione</option>
            {(codes.cstIbsCbs ?? []).map((opt: any) => (
              <option key={opt.code} value={opt.code}>{opt.code} — {opt.description}</option>
            ))}
          </select>
        </StyledFormGroup>

        <StyledFormGroup>
          <LabelWithInfo label="Classificação tributária (cClassTrib)" tooltip="Código pareado ao CST IBS/CBS — obrigatório para emitir" />
          <select
            value={scenario.classificacaoTributaria}
            onChange={(e) => updateScenario(scenario.id, 'classificacaoTributaria', e.target.value)}
            disabled={!selectedIbsCbs}
          >
            <option value="">{selectedIbsCbs ? 'Selecione' : 'Selecione o CST IBS/CBS antes'}</option>
            {classTribFiltered.map((opt: any) => (
              <option key={opt.code} value={opt.code}>{opt.code} — {opt.description}</option>
            ))}
          </select>
        </StyledFormGroup>

        <StyledFormGroup>
          <LabelWithInfo label="Alíquota CBS (%)" tooltip="Alíquota da CBS" />
          <input type="number" value={scenario.aliquotaCBS} onChange={(e) => updateScenario(scenario.id, 'aliquotaCBS', Number(e.target.value))} />
        </StyledFormGroup>

        <StyledFormGroup>
          <LabelWithInfo label="Alíquota IBS (%)" tooltip="Alíquota da IBS" />
          <input type="number" value={scenario.aliquotaIBS} onChange={(e) => updateScenario(scenario.id, 'aliquotaIBS', Number(e.target.value))} />
        </StyledFormGroup>
      </FormGrid>

      <div style={{ marginTop: '25px' }}>
        <label style={{ fontSize: '13px', color: '#666', display: 'block', marginBottom: '10px' }}>Aplicar a:</label>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          {REGION_OPTIONS.map((region) => (
            <RegionButton
              key={region.value}
              $active={scenario.regions?.includes(region.value)}
              onClick={() => {
                const currentRegions = scenario.regions || [];
                const newRegions = currentRegions.includes(region.value)
                  ? currentRegions.filter((r: string) => r !== region.value)
                  : [...currentRegions, region.value];
                updateScenario(scenario.id, 'regions', newRegions);
              }}
            >
              {region.label}
            </RegionButton>
          ))}

          <RegionButton
            $active={scenario.applyToSameState}
            onClick={() => updateScenario(scenario.id, 'applyToSameState', !scenario.applyToSameState)}
          >
            Mesmo estado
          </RegionButton>

          <RegionButton
            $active={scenario.showStateInput}
            onClick={() => updateScenario(scenario.id, 'showStateInput', !scenario.showStateInput)}
          >
            Por estado
          </RegionButton>

          {scenario.showStateInput && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', position: 'relative' }}>
              <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#333', marginLeft: '8px' }}>
                Estados específicos:
              </label>

              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: '6px', background: '#ebebeb',
                padding: '8px 15px', borderRadius: '100px', width: '350px',
                minHeight: '42px', alignItems: 'center', border: '1px solid #ddd'
              }}>
                {allStates.filter((s: any) => scenario.stateIds?.includes(s.id)).map((state: any) => (
                  <span key={state.id} style={{
                    background: '#fff', padding: '2px 10px', borderRadius: '12px',
                    fontSize: '12px', fontWeight: 'bold', display: 'flex',
                    alignItems: 'center', gap: '5px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    {state.uf}
                    <button
                      type="button"
                      onClick={() => updateScenario(scenario.id, 'stateIds', scenario.stateIds.filter((id: number) => id !== state.id))}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#ff4d4d', fontSize: '14px' }}
                    >
                      ×
                    </button>
                  </span>
                ))}

                <input
                  type="text"
                  placeholder={scenario.stateIds?.length > 0 ? "" : "Buscar estado..."}
                  value={stateSearch}
                  onFocus={() => setIsStateListOpen(true)}
                  onClick={() => setIsStateListOpen(true)}
                  onChange={(e) => setStateSearch(e.target.value)}
                  onBlur={() => {
                    setTimeout(() => {
                      setIsStateListOpen(false);
                      setStateSearch("");
                    }, 200);
                  }}
                  style={{ background: 'transparent', border: 'none', outline: 'none', fontSize: '14px', flex: 1, minWidth: '80px' }}
                />
              </div>

              {isStateListOpen && (
                <DropdownList style={{ width: '350px' }}>
                  {allStates
                    .filter((st: any) =>
                      !stateSearch ||
                      st.name.toLowerCase().includes(stateSearch.toLowerCase()) ||
                      st.uf.toLowerCase().includes(stateSearch.toLowerCase())
                    )
                    .map((state: any) => (
                      <DropdownItem
                        key={state.id}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const currentIds = scenario.stateIds || [];
                          if (!currentIds.includes(state.id)) {
                            updateScenario(scenario.id, 'stateIds', [...currentIds, state.id]);
                          }
                          setStateSearch("");
                        }}
                      >
                        <strong>{state.uf}</strong> — {state.name}
                      </DropdownItem>
                    ))}
                </DropdownList>
              )}
            </div>
          )}
        </div>
      </div>
    </StyledCard>
  );
};
